import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Filter,
  Download,
  RefreshCw,
  Search,
  X,
  FileText,
  User as UserIcon,
  Building2,
} from 'lucide-react';
import { contractService, erpDataService } from './_shared/api';
import type { Contract, Vendedor, ApprovalStatus } from './_shared/types';
import ContractDetailModal from './_components/ContractDetailModal';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Prospect > Launched — lista de contratos lançados pelos vendedores.
 *
 * Versão admin-only do apps/prospect-web/src/pages/Launched.tsx (401 lns).
 * Diferenças:
 *   - Sem botão de edit (admin não edita rascunho de vendedor)
 *   - Usa ContractDetailModal admin-only (sem upload/appointment/edit)
 *
 * Aceita ?vendedorId= como query param (Dashboard linka pra cá filtrado).
 */

interface Filters {
  vendedorId?: number;
  dateFrom: string;
  dateTo: string;
  approvalStatuses: ApprovalStatus[];
}

const APPROVAL_BADGE: Record<ApprovalStatus, { label: string; cls: string }> = {
  pending: { label: 'Pendente', cls: 'bg-warning-solid text-white' },
  approved: { label: 'Aprovado', cls: 'bg-success-solid text-white' },
  rejected: { label: 'Reprovado', cls: 'bg-danger-solid text-white' },
  declined: { label: 'Declinado', cls: 'bg-warning-solid-hover text-white' },
};

function parseFilterDate(br: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br);
  if (!m) return null;
  const dt = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function maskDate(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export default function ProspectLaunched() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialVendedorId = searchParams.get('vendedorId');
  const initialOpenContractId = searchParams.get('openContractId');

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    initialOpenContractId,
  );
  const [filters, setFilters] = useState<Filters>({
    vendedorId: initialVendedorId ? Number(initialVendedorId) : undefined,
    dateFrom: '',
    dateTo: '',
    approvalStatuses: [],
  });

  async function load(): Promise<void> {
    setRefreshing(true);
    try {
      const list = await contractService.list();
      setContracts(list);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    erpDataService.getVendedores().then(setVendedores).catch(() => {
      // silent — admin pode estar sem credentials ERP
    });
  }, []);

  useEffect(() => {
    if (initialVendedorId || initialOpenContractId) {
      searchParams.delete('vendedorId');
      searchParams.delete('openContractId');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    let result = contracts;
    if (filters.vendedorId) result = result.filter((c) => c.vendedorId === filters.vendedorId);
    if (filters.approvalStatuses.length > 0) {
      result = result.filter((c) => filters.approvalStatuses.includes(c.approvalStatus));
    }
    if (filters.dateFrom) {
      const from = parseFilterDate(filters.dateFrom);
      if (from) result = result.filter((c) => new Date(c.createdAt) >= from);
    }
    if (filters.dateTo) {
      const to = parseFilterDate(filters.dateTo);
      if (to) {
        const toEnd = new Date(to);
        toEnd.setHours(23, 59, 59, 999);
        result = result.filter((c) => new Date(c.createdAt) <= toEnd);
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (c) =>
          c.clienteNome?.toLowerCase().includes(q) ||
          c.erpContratoId?.toString().includes(q) ||
          c.popNome?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [contracts, filters, search]);

  const activeFilterCount =
    (filters.vendedorId ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.approvalStatuses.length > 0 ? 1 : 0);

  async function handleExport(): Promise<void> {
    setExporting(true);
    try {
      await contractService.exportXlsx(filters);
    } catch (err) {
      const e = err as { message?: string };
      alert('Erro ao exportar: ' + (e.message ?? 'desconhecido'));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-fg">Prospect — Contratos</h1>
          <p className="text-sm text-fg-2">
            {filtered.length} contrato(s) {activeFilterCount > 0 && '· filtros ativos'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilterOpen(true)}
            className="relative w-10 h-10 rounded-full bg-canvas hover:bg-hover border border-line flex items-center justify-center transition"
            aria-label="Filtros"
          >
            <Filter
              size={18}
              className={activeFilterCount > 0 ? 'text-primary' : 'text-fg-2'}
            />
            {activeFilterCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={() => void handleExport()}
            disabled={exporting || filtered.length === 0}
            className="w-10 h-10 rounded-full bg-canvas hover:bg-hover border border-line flex items-center justify-center disabled:opacity-40 transition"
            aria-label="Exportar XLSX"
          >
            {exporting ? (
              <RefreshCw size={18} className="animate-spin text-success" />
            ) : (
              <Download size={18} className="text-success" />
            )}
          </button>
          <button
            onClick={() => void load()}
            disabled={refreshing}
            className="w-10 h-10 rounded-full bg-canvas hover:bg-hover border border-line flex items-center justify-center transition"
            aria-label="Atualizar"
          >
            <RefreshCw
              size={18}
              className={refreshing ? 'animate-spin text-primary' : 'text-fg-2'}
            />
          </button>
        </div>
      </header>

      {/* Search */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por cliente, # contrato ou POP..."
          className="w-full bg-canvas border border-line rounded-xl pl-9 pr-9 h-11 text-fg placeholder:text-muted outline-none focus:border-primary"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-fg-2"
            aria-label="Limpar busca"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw size={28} className="animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface border border-line rounded-2xl p-10 text-center">
          <FileText size={36} className="text-faint mx-auto mb-3" />
          <p className="text-fg font-semibold mb-1">
            {activeFilterCount > 0 || search ? 'Nenhum resultado' : 'Nenhum contrato'}
          </p>
          <p className="text-muted text-sm">
            {activeFilterCount > 0 || search
              ? 'Ajuste os filtros aplicados.'
              : 'Os contratos lançados aparecerão aqui.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {filtered.map((c) => {
            const ap = APPROVAL_BADGE[c.approvalStatus] ?? APPROVAL_BADGE.pending;
            const isPF = c.tipo === 'F';
            return (
              <li key={c.id}>
                <button
                  onClick={() => setSelectedContractId(c.id)}
                  className="w-full text-left bg-surface border border-line hover:border-primary-solid rounded-2xl p-4 transition flex items-center"
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${
                      isPF ? 'bg-primary-solid/20' : 'bg-info-solid/20'
                    }`}
                  >
                    {isPF ? (
                      <UserIcon size={18} className="text-primary" />
                    ) : (
                      <Building2 size={18} className="text-info" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {c.erpContratoId && (
                        <span className="text-xs font-bold text-primary">
                          #{c.erpContratoId}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${ap.cls}`}
                      >
                        {ap.label}
                      </span>
                    </div>
                    <p className="text-fg font-medium truncate">
                      {c.clienteNome || '—'}
                    </p>
                    <p className="text-xs text-muted truncate">
                      <span className="text-fg-2">{c.vendedorNome || '—'}</span> ·{' '}
                      {c.popNome || 'POP n/d'} ·{' '}
                      {new Date(c.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal de filtros */}
      {filterOpen && (
        <FilterModal
          filters={filters}
          vendedores={vendedores}
          onApply={(f) => {
            setFilters(f);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {/* Modal de detalhes */}
      <ContractDetailModal
        contractId={selectedContractId}
        onClose={() => setSelectedContractId(null)}
        onChanged={() => void load()}
      />
    </div>
  );
}

function FilterModal({
  filters,
  vendedores,
  onApply,
  onClose,
}: {
  filters: Filters;
  vendedores: Vendedor[];
  onApply: (f: Filters) => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState<Filters>(filters);

  function toggleStatus(s: ApprovalStatus): void {
    setLocal((p) => ({
      ...p,
      approvalStatuses: p.approvalStatuses.includes(s)
        ? p.approvalStatuses.filter((x) => x !== s)
        : [...p.approvalStatuses, s],
    }));
  }

  // Esc fecha; Enter aplica os filtros; clique fora NÃO fecha (Fase 30)
  useModalKeys(onClose, () => onApply(local));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl max-w-md w-full p-5 border border-line">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-fg">Filtros</h2>
          <button onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase text-fg-2 mb-1.5 block">Vendedor</label>
            <select
              value={local.vendedorId ?? ''}
              onChange={(e) =>
                setLocal({
                  ...local,
                  vendedorId: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full bg-canvas border border-line rounded-xl h-11 px-3 text-fg outline-none focus:border-primary"
            >
              <option value="">Todos os vendedores</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.erpId}>
                  {v.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase text-fg-2 mb-1.5 block">Período</label>
            <div className="flex flex-col gap-2">
              <input
                placeholder="De DD/MM/AAAA"
                value={local.dateFrom}
                onChange={(e) => setLocal({ ...local, dateFrom: maskDate(e.target.value) })}
                className="w-full bg-canvas border border-line rounded-xl h-11 px-3 text-fg outline-none focus:border-primary placeholder:text-muted"
              />
              <input
                placeholder="Até DD/MM/AAAA"
                value={local.dateTo}
                onChange={(e) => setLocal({ ...local, dateTo: maskDate(e.target.value) })}
                className="w-full bg-canvas border border-line rounded-xl h-11 px-3 text-fg outline-none focus:border-primary placeholder:text-muted"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase text-fg-2 mb-1.5 block">Status</label>
            <div className="flex gap-2">
              {(['pending', 'approved', 'rejected', 'declined'] as ApprovalStatus[]).map((s) => {
                const active = local.approvalStatuses.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`flex-1 h-10 rounded-xl text-xs font-bold border transition ${
                      active
                        ? `${APPROVAL_BADGE[s].cls} border-transparent`
                        : 'bg-canvas border-line text-fg-2 hover:bg-hover'
                    }`}
                  >
                    {APPROVAL_BADGE[s].label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={() =>
              onApply({
                vendedorId: undefined,
                dateFrom: '',
                dateTo: '',
                approvalStatuses: [],
              })
            }
            className="flex-1 h-11 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
          >
            Limpar
          </button>
          <button
            onClick={() => onApply(local)}
            className="flex-1 h-11 bg-primary-solid hover:bg-primary-solid-hover rounded-xl text-white font-bold"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
